/**
 * Chat link rules — the TypeScript twin of Grains/Webinar/ChatLinkRules.cs. Both are pinned by
 * Grains.Tests/ChatLinkRules.golden.json. Isomorphic: only the WHATWG URL class, no DOM access,
 * because chat pages are server-rendered.
 */

/** camelCased SignalR payload of Grains.Interfaces/Settings/Models/ChatLinkPolicy.cs */
export interface ChatLinkPolicy {
    allowedDomains: string[];
    blockedDomains: string[];
    selfHost: string;
}

export const MAX_DISTINCT_CHAT_URLS = 10;

export type ChatTextToken = { type: 'text'; value: string } | { type: 'link'; raw: string; url: URL };
export type ChatLinkKind = 'self' | 'blocked' | 'allowed' | 'unknown';
export type ChatLinkVerdict = 'none' | 'ok' | 'blocked' | 'tooMany' | 'invalid';

// The candidate must end on exactly the same code points in both twins, so the boundary is spelled
// out instead of using \s: JavaScript counts U+FEFF as whitespace but not U+0085, .NET counts
// U+0085 but not U+FEFF. The union of both engines' \s with those two is this class.
const CANDIDATE_SOURCE = 'https://[^\\s\\u0085\\uFEFF]+';
const AUTHORITY = /^https:\/\/([^/?#]*)/i;
// Characters whose handling differs between System.Uri and this parser (backslash-as-slash,
// percent-decoded hosts, userinfo, IP literals): invalid on both sides, so what the server checked
// is what the browser opens.
const FORBIDDEN_AUTHORITY = /[\\%@[\]]/;
const IPV4 = /^\d{1,3}(\.\d{1,3}){3}$/;
// Sentence punctuation after a pasted URL is not part of it (documented limitation for URLs that end in one).
const TRAILING_PUNCTUATION = /[).,!?;:'"\]]+$/;

const parseStrict = (candidate: string): URL | null => {
    const authority = AUTHORITY.exec(candidate)?.[1] ?? '';
    if (!authority || FORBIDDEN_AUTHORITY.test(authority)) return null;
    let url: URL;
    try {
        url = new URL(candidate);
    } catch {
        return null;
    }
    if (url.protocol !== 'https:' || !url.hostname || url.username || url.password) return null;
    if (IPV4.test(url.hostname)) return null;
    // Keep the fragment on the parsed URL: render and open the re-serialized url.href, so what is
    // opened is exactly what was matched. The fragment is stripped only for the dedup/provider key
    // (extractHttpsUrls below), never from the URL itself.
    return url;
};

/** Dedup/provider key: fragment removed, path and query kept — never reduced to the hostname. */
const canonicalKey = (url: URL): string => {
    // Always rebuild: `https://a.example/p#` reports an empty hash but keeps the `#` in href, and the
    // server (GetLeftPart(UriPartial.Query)) counts it as the same URL as `https://a.example/p`.
    const withoutFragment = new URL(url.href);
    withoutFragment.hash = '';
    return withoutFragment.href;
};

/** Lower-case, trailing root dot trimmed. The URL parser already delivers punycode. */
export const normalizeHostname = (hostname: string): string => hostname.trim().replace(/\.$/, '').toLowerCase();

export const isSelf = (hostname: string, selfHost: string): boolean => selfHost.length > 0 && normalizeHostname(hostname) === selfHost.toLowerCase();

/** An entry matches its own host and every sub-domain, never a lookalike (evil-example.com). */
export const matchesDomain = (hostname: string, domains: string[]): boolean => {
    const host = normalizeHostname(hostname);
    return domains.some((domain) => host === domain.toLowerCase() || host.endsWith('.' + domain.toLowerCase()));
};

export const tokenizeChatText = (text: string): ChatTextToken[] => {
    const tokens: ChatTextToken[] = [];
    let last = 0;
    for (const match of text.matchAll(new RegExp(CANDIDATE_SOURCE, 'gi'))) {
        const start = match.index ?? 0;
        const raw = match[0].replace(TRAILING_PUNCTUATION, '');
        const url = parseStrict(raw);
        if (!url) continue; // an unparseable candidate stays plain text (the server refuses new ones)
        if (start > last) tokens.push({ type: 'text', value: text.slice(last, start) });
        tokens.push({ type: 'link', raw, url });
        last = start + raw.length;
    }
    if (last < text.length) tokens.push({ type: 'text', value: text.slice(last) });
    return tokens;
};

export const extractHttpsUrls = (texts: (string | null | undefined)[]): { urls: URL[]; invalidCandidates: number } => {
    const urls: URL[] = [];
    const seen = new Set<string>();
    let invalidCandidates = 0;
    for (const text of texts) {
        if (!text) continue;
        for (const match of text.matchAll(new RegExp(CANDIDATE_SOURCE, 'gi'))) {
            const url = parseStrict(match[0].replace(TRAILING_PUNCTUATION, ''));
            if (!url) {
                invalidCandidates++;
                continue;
            }
            const key = canonicalKey(url);
            if (!seen.has(key)) {
                seen.add(key);
                urls.push(url); // keeps the fragment of the first occurrence; later duplicates differing only in fragment are dropped
            }
        }
    }
    return { urls, invalidCandidates };
};

/** Precedence self > block-list > allow-list; the allow-list only decides whether the click warns. */
export const classifyUrl = (url: URL, policy: ChatLinkPolicy): ChatLinkKind => {
    if (isSelf(url.hostname, policy.selfHost)) return 'self';
    if (matchesDomain(url.hostname, policy.blockedDomains)) return 'blocked';
    if (matchesDomain(url.hostname, policy.allowedDomains)) return 'allowed';
    return 'unknown';
};

/** The pre-send check; mirrors ChatLinkRules.Evaluate so the client refuses what the server would refuse. */
export const evaluateChatLinks = (texts: (string | null | undefined)[], policy: ChatLinkPolicy): ChatLinkVerdict => {
    const { urls, invalidCandidates } = extractHttpsUrls(texts);
    if (invalidCandidates > 0) return 'invalid';
    const remaining = urls.filter((url) => !isSelf(url.hostname, policy.selfHost));
    if (remaining.some((url) => matchesDomain(url.hostname, policy.blockedDomains))) return 'blocked';
    if (remaining.length > MAX_DISTINCT_CHAT_URLS) return 'tooMany';
    if (remaining.length === 0) return 'none';
    return 'ok';
};
